import * as React from 'react';
import { Link } from 'react-router-dom';
import type { LinkProps } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import type { VariantProps } from 'class-variance-authority';

import { cn } from '@ui/lib/utils';

import { Button, buttonVariants } from './button';

/** MUI LoadingButton compatibility: variants and sizes map to shadcn `Button` tokens. */
export type LoadingButtonProps = {
  loading?: boolean;
  variant?: 'contained' | 'outlined' | 'text' | NonNullable<VariantProps<typeof buttonVariants>['variant']>;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  fullWidth?: boolean;
  size?: 'small' | 'medium' | 'large';
  /** MUI parity — ignored for styling; primary is default shadcn look */
  color?: 'primary' | 'secondary' | 'inherit';
  component?: typeof Link;
  to?: LinkProps['to'];
  state?: LinkProps['state'];
} & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'color'>;

function mapVariant(
  variant: LoadingButtonProps['variant'],
): NonNullable<VariantProps<typeof buttonVariants>['variant']> {
  if (variant === 'outlined') return 'outline';
  if (variant === 'text') return 'ghost';
  if (variant === 'contained') return 'default';
  return variant ?? 'default';
}

function mapSize(size?: LoadingButtonProps['size']): NonNullable<VariantProps<typeof buttonVariants>['size']> {
  if (size === 'small') return 'sm';
  if (size === 'large') return 'lg';
  return 'default';
}

export const LoadingButton = React.forwardRef<HTMLButtonElement | HTMLAnchorElement, LoadingButtonProps>(
  (props, ref) => {
    const {
      loading = false,
      variant = 'contained',
      startIcon,
      endIcon,
      fullWidth,
      size = 'medium',
      className,
      children,
      disabled,
      // MUI parity; not forwarded to DOM
      // eslint-disable-next-line @typescript-eslint/no-unused-vars -- stripped intentionally
      color,
      component,
      to,
      state,
      type = 'button',
      onClick,
      ...rest
    } = props;

    const v = mapVariant(variant);
    const sz = mapSize(size);
    const isDisabled = Boolean(disabled || loading);

    let linkStart: React.ReactNode = null;
    if (loading) {
      linkStart = <Loader2 className="size-4 shrink-0 animate-spin" aria-hidden />;
    } else if (startIcon) {
      linkStart = <span className="inline-flex shrink-0 [&_svg]:size-4">{startIcon}</span>;
    }

    const linkInner = (
      <>
        {linkStart}
        {children}
        {!loading && endIcon ? <span className="inline-flex shrink-0 [&_svg]:size-4">{endIcon}</span> : null}
      </>
    );

    const baseClass = cn(
      buttonVariants({ variant: v, size: sz }),
      'inline-flex items-center justify-center gap-1.5',
      fullWidth && 'w-full',
      className,
    );

    if (component === Link && to !== undefined) {
      return (
        <Link
          ref={ref as React.Ref<HTMLAnchorElement>}
          to={to}
          state={state}
          className={cn(baseClass, isDisabled && 'pointer-events-none opacity-50')}
          aria-disabled={isDisabled || undefined}
          tabIndex={isDisabled ? -1 : undefined}
          onClick={e => {
            if (isDisabled) {
              e.preventDefault();
              return;
            }
            onClick?.(e as unknown as React.MouseEvent<HTMLButtonElement>);
          }}
        >
          {linkInner}
        </Link>
      );
    }

    return (
      <Button
        ref={ref as React.Ref<HTMLElement>}
        type={type}
        variant={v}
        size={sz}
        loading={loading}
        disabled={disabled}
        className={cn(fullWidth && 'w-full', className)}
        onClick={onClick}
        {...rest}
      >
        {!loading && startIcon ? <span className="inline-flex shrink-0 [&_svg]:size-4">{startIcon}</span> : null}
        {children}
        {!loading && endIcon ? <span className="inline-flex shrink-0 [&_svg]:size-4">{endIcon}</span> : null}
      </Button>
    );
  },
);

LoadingButton.displayName = 'LoadingButton';

export default LoadingButton;
